import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Eye, Pencil, Plus } from "lucide-react";
import { entitiesApi } from "@/api/counterparties";
import { ApiError } from "@/api/client";
import type { EntityListItemDto } from "@/types/counterparties";
import { COUNTERPARTY_PERMISSIONS } from "@/types/counterparties";
import { Button, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { Pagination } from "@/components/layout/Pagination";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { RowActions, type RowAction } from "@/components/ux";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { useToast } from "@/components/ui/Toast";
import { CounterpartyStatusBadge } from "@/components/counterparties/StatusBadge";
import { ActiveDealsBlockedDialog } from "@/components/counterparties/ActiveDealsBlockedDialog";
import { useDateFormatter } from "@/utils/datetime";
import { entityKeys, invalidateCounterpartyReads } from "../counterpartyKeys";

const PERMS = COUNTERPARTY_PERMISSIONS.Entity;
const PAGE_SIZE = 25;

export function EntityListPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const dialogs = useDialogs();
  const toast = useToast();
  const navigate = useNavigate();
  const { date } = useDateFormatter();

  const canCreate = hasPermission(PERMS.Create);
  const canUpdate = hasPermission(PERMS.Update);
  const canDelete = hasPermission(PERMS.Delete);

  const [search, setSearch] = useState("");
  const [archiveScope, setArchiveScope] = useState<"Active" | "All" | "Archived">("Active");
  const [blockedFor, setBlockedFor] = useState<EntityListItemDto | null>(null);
  const [page, setPage] = useState(1);

  useBreadcrumbs([{ label: "Entities" }]);

  useEffect(() => {
    setPage(1);
  }, [search, archiveScope]);

  const entitiesQuery = useQuery({
    queryKey: entityKeys.list({ search, archiveScope, page }),
    queryFn: () =>
      entitiesApi.list({
        q: search || undefined,
        includeArchived: archiveScope === "All",
        archivedOnly: archiveScope === "Archived",
        page,
        pageSize: PAGE_SIZE,
      }),
  });

  const totalPages = useMemo(() => {
    const data = entitiesQuery.data;
    if (!data || data.pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  }, [entitiesQuery.data]);

  const archive = useMutation({
    mutationFn: (id: string) => entitiesApi.archive(id),
    onSuccess: () => {
      invalidateCounterpartyReads(queryClient, "entity");
      toast.success("Entity archived.");
    },
    onError: (e, id) => {
      if (e instanceof ApiError && e.status === 409) {
        const row = (entitiesQuery.data?.data ?? []).find((p) => p.id === id);
        if (row) setBlockedFor(row);
      } else {
        toast.error(e instanceof Error ? e.message : "Archive failed.");
      }
    },
  });

  const rows = entitiesQuery.data?.data ?? [];

  const columns: Column<EntityListItemDto>[] = [
    {
      key: "name",
      header: "Legal entity",
      render: (e) => (
        <Link to={`/counterparties/entities/${e.id}`} className="font-semibold hover:text-gold-shadow">
          {e.legalEntityName}
          {e.dba && <span className="text-caption text-gray-mid font-normal"> · DBA {e.dba}</span>}
        </Link>
      ),
    },
    { key: "type", header: "Entity type", render: (e) => e.entityType },
    { key: "stateOfFormation", header: "State", render: (e) => e.stateOfFormation ?? "—" },
    {
      key: "status",
      header: "Status",
      render: (e) => <CounterpartyStatusBadge status={e.status} />,
    },
    {
      key: "created",
      header: "Created",
      render: (e) => date(e.createdAt),
    },
    {
      key: "actions",
      header: "",
      className: "w-10 text-right",
      render: (e) => (
        <RowActions
          actions={[
            { key: "view", label: "View", icon: Eye, href: `/counterparties/entities/${e.id}` },
            {
              key: "edit",
              label: "Edit",
              icon: Pencil,
              hidden: !canUpdate,
              href: `/counterparties/entities/${e.id}/edit`,
            },
            {
              key: "archive",
              label: "Archive",
              icon: Archive,
              destructive: true,
              hidden: !canDelete || e.status === "Archived",
              onClick: async () => {
                const ok = await dialogs.confirm({
                  title: `Archive ${e.legalEntityName}?`,
                  message: "This entity will no longer appear in active lists.",
                  confirmLabel: "Archive",
                  destructive: true,
                });
                if (!ok) return;
                archive.mutate(e.id);
              },
            },
          ] satisfies RowAction[]}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Counterparties"
        title="Entities"
        actions={
          canCreate && (
            <Button size="sm" onClick={() => navigate("/counterparties/entities/new")}>
              <Plus size={14} strokeWidth={1.5} aria-hidden /> New entity
            </Button>
          )
        }
      />

      <div className="flex items-center gap-space-3 mb-space-3">
        <Input
          type="search"
          placeholder="Search by legal name, DBA, state of formation…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="grow"
          aria-label="Search entities"
        />
        <Select
          value={archiveScope}
          onChange={(e) => setArchiveScope(e.target.value as typeof archiveScope)}
          className="max-w-[10rem] shrink-0"
          aria-label="Archive scope"
        >
          <option value="Active">Active</option>
          <option value="All">All</option>
          <option value="Archived">Archived</option>
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={entitiesQuery.isLoading}
        error={entitiesQuery.isError ? (entitiesQuery.error as Error).message : null}
        emptyMessage={
          canCreate
            ? "No entities match your filters. Click “New entity” to add one."
            : "No entities match your filters."
        }
        rowKey={(e) => e.id}
      />

      {entitiesQuery.data && entitiesQuery.data.totalCount > 0 && (
        <Pagination
          page={entitiesQuery.data.page}
          pageSize={entitiesQuery.data.pageSize}
          totalCount={entitiesQuery.data.totalCount}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      )}

      {blockedFor && (
        <ActiveDealsBlockedDialog
          open
          displayName={blockedFor.legalEntityName}
          onClose={() => setBlockedFor(null)}
        />
      )}
    </>
  );
}
